import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import some from 'lodash/some';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment, Button, Message } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import {
  updateZutatengruppe,
  getZutatengruppeByID,
  getZutatengruppen,
  deleteZutatengruppe,
  getVistPermissions,
} from '../../../actions';

import ButtonArea from '../ButtonArea';

import ZutatengruppeForm from './ZutatengruppeForm';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const ZutatengruppeEdit = ({
  pathname,
  zutatengruppe,
  zutatengruppen,
  getZutatengruppen,
  getZutatengruppeByID,
  updateZutatengruppe,
  deleteZutatengruppe,
  intl,
  token,
  permissions = null,
  getVistPermissions,
}) => {
  const location = useLocation();

  const [formData, setFormData] = useState({
    name: '',
  });

  const [error, setError] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [isZutatengruppeNameDuplicate, setIsZutatengruppeNameDuplicate] =
    useState(false);
  const isSubmitDisabled = formData.name === '';

  const history = useHistory();
  // Define the allowed user groups for the whole view
  const allowedUserGroups = [1];
  // flag to make sure permissions are fully loaded before returning unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Check if permissions are available, if not, fetch them
    if (!permissions.length && permissionsLoading) {
      getVistPermissions()
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch((error) => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setIsClient(true);
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');
    getZutatengruppen({ showAll: true });
    getZutatengruppeByID(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getZutatengruppeByID, getZutatengruppen]);

  useEffect(() => {
    if (zutatengruppe) {
      setFormData({
        name: zutatengruppe.name,
      });
    }
  }, [zutatengruppe]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));

    if (name === 'name' && value !== zutatengruppe.name) {
      const isDuplicate = zutatengruppen.some(
        (zutatengruppe) => zutatengruppe.name === value,
      );
      setIsZutatengruppeNameDuplicate(isDuplicate);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    try {
      await updateZutatengruppe(id, formData);
      history.push(`/controlpanel/rezeptdatenbank/zutatengruppen`);
      // Handle success or show a success message
    } catch (error) {
      // Handle error during update
      setError(error);
    }
  };

  const handleDelete = async () => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    try {
      await deleteZutatengruppe(id);
      setDeleteSuccess(true);
    } catch (error) {
      // Handle delete error
      setError(error);
    }
  };

  if (!token) {
    return <Error error={{ status: 401 }} />;
  }
  if (
    !permissionsLoading &&
    !some(allowedUserGroups, (group) => permissions.includes(group))
  ) {
    return <Error error={{ status: 401 }} />;
  }
  return (
    <div className="view-wrapper">
      <Helmet title="Zutatengruppe Bearbeiten" />
      <Container className="controlpanel">
        <Segment.Group raised>
          <Segment className="primary">
            <div className="rezeptdatenbank header">
              <div className="header-container">
                <h1>{zutatengruppe && zutatengruppe.name} </h1>
              </div>
              <div className="button-container">
                <ButtonArea
                  handleSubmit={handleSubmit}
                  handleDelete={handleDelete}
                  isSubmitDisabled={isSubmitDisabled}
                  backURL={'/controlpanel/rezeptdatenbank/zutatengruppen'}
                  typeString={'die Zutatengruppe'}
                />
              </div>
            </div>
            <div className="rezeptdatenbank header"></div>
          </Segment>
          <Segment className="rdbAddEditForm">
            {error && (
              <Message negative>
                <Message.Header>Error</Message.Header>
                <p>{error.message}</p>
              </Message>
            )}
            {deleteSuccess ? (
              <Message positive>
                <Message.Header>Zutatengruppe wurde gelöscht</Message.Header>
                <Button
                  primary
                  as={Link}
                  to="/controlpanel/rezeptdatenbank/zutatengruppen"
                >
                  Zurück zur Zutatengruppen-Übersicht
                </Button>
              </Message>
            ) : (
              <>
                <div className="rdb edit form zutatengruppen">
                  <ZutatengruppeForm
                    formData={formData}
                    isZutatengruppeNameDuplicate={isZutatengruppeNameDuplicate}
                    handleInputChange={handleInputChange}
                  />
                </div>
              </>
            )}
          </Segment>
        </Segment.Group>
      </Container>
      {isClient && (
        <Portal node={document.getElementById('toolbar')}>
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/controlpanel/zutatengruppedatenbank" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />
        </Portal>
      )}
    </div>
  );
};

ZutatengruppeEdit.propTypes = {
  pathname: PropTypes.string.isRequired,
  zutatengruppen: PropTypes.array.isRequired,
  zutatengruppe: PropTypes.object,
  getZutatengruppeByID: PropTypes.func.isRequired,
  updateZutatengruppe: PropTypes.func.isRequired,
  deleteZutatengruppe: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  token: PropTypes.string,
  permissions: PropTypes.array,
  getVistPermissions: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      pathname: state.router.location.pathname,
      zutatengruppe: state.zutatengruppeByID.data,
      zutatengruppen: state.zutatengruppen.data.zutatengruppen,
      token: state.userSession.token,
      permissions: state.permissions.data,
    }),
    {
      updateZutatengruppe,
      getZutatengruppeByID,
      deleteZutatengruppe,
      getZutatengruppen,
      getVistPermissions,
    },
  ),
)(ZutatengruppeEdit);
